import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { formatVnd } from '../lib/format';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { PageHeader } from '@/components/layout/page-header';

export function AdminCardsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-cards'],
    queryFn: () => api.listCards(),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.deleteCard(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-cards'] });
      qc.invalidateQueries({ queryKey: ['cards-lookup'] });
    },
  });
  const reorder = useMutation({
    mutationFn: (ids: number[]) => api.reorderCards(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-cards'] });
      qc.invalidateQueries({ queryKey: ['cards-lookup'] });
    },
  });

  function move(idx: number, direction: -1 | 1) {
    if (!data) return;
    const ids = data.rows.map((c) => c.id);
    const target = idx + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[idx], ids[target]] = [ids[target]!, ids[idx]!];
    reorder.mutate(ids);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cards"
        actions={
          <Link
            to="/admin/cards/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <Plus size={14} strokeWidth={1.75} />
            Add card
          </Link>
        }
      />
      {isLoading && <Skeleton className="h-40 w-full" />}
      {data && data.rows.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
          {`Chưa có thẻ — bấm "Add card" để đăng ký.`}
        </div>
      )}
      {data && data.rows.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-card md:block">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 border-b border-border bg-background text-left">
                <tr>
                  <th className="w-16 p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Order
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Bank
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Last 4
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Alias
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Owner
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                  <th className="p-3 text-right font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Limit/month
                  </th>
                  <th className="p-3 text-right font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Tx this month
                  </th>
                  <th className="p-3"> </th>
                </tr>
              </thead>
              <tbody className="[&_tr:nth-child(even)]:bg-background">
                {data.rows.map((c, idx) => (
                  <tr
                    key={c.id}
                    className="border-b border-border last:border-0 hover:bg-background"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => move(idx, -1)}
                          disabled={idx === 0 || reorder.isPending}
                          className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
                          aria-label="Move up"
                        >
                          <ChevronUp size={14} strokeWidth={1.75} />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(idx, 1)}
                          disabled={idx === data.rows.length - 1 || reorder.isPending}
                          className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
                          aria-label="Move down"
                        >
                          <ChevronDown size={14} strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-muted-foreground">{c.bank_code}</td>
                    <td className="p-3 font-mono text-muted-foreground">{c.last4}</td>
                    <td className="p-3 text-foreground">{c.alias}</td>
                    <td className="p-3 text-muted-foreground">
                      {c.owning_department_code ? (
                        <span>
                          <span className="font-mono text-xs">{c.owning_department_code}</span>
                          <span className="ml-1.5 text-foreground">{c.owning_department_name}</span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="p-3">
                      <Badge
                        variant={
                          c.status === 'active'
                            ? 'success'
                            : c.status === 'frozen'
                              ? 'warning'
                              : 'default'
                        }
                      >
                        {c.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">
                      {c.monthly_limit_vnd != null ? formatVnd(c.monthly_limit_vnd) : '—'}
                    </td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">
                      {c.tx_count_this_month}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/cards/${c.id}/edit`)}
                        className="mr-1 text-primary"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Soft-delete card ${c.bank_code}•${c.last4}?`))
                            remove.mutate(c.id);
                        }}
                        className="text-destructive"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {data.rows.map((c, idx) => (
              <div key={c.id} className="rounded-lg border border-border bg-card p-3 text-sm">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-xs text-muted-foreground">
                    {c.bank_code} · <span className="tracking-widest">••••{c.last4}</span>
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0 || reorder.isPending}
                      className="rounded p-1 text-muted-foreground hover:bg-background disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <ChevronUp size={14} strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={idx === data.rows.length - 1 || reorder.isPending}
                      className="rounded p-1 text-muted-foreground hover:bg-background disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <ChevronDown size={14} strokeWidth={1.75} />
                    </button>
                    <Badge
                      variant={
                        c.status === 'active'
                          ? 'success'
                          : c.status === 'frozen'
                            ? 'warning'
                            : 'default'
                      }
                      className="ml-1"
                    >
                      {c.status}
                    </Badge>
                  </div>
                </div>
                <div className="mb-1 font-medium text-foreground">{c.alias}</div>
                <div className="mb-1 text-xs text-muted-foreground">
                  Owner:{' '}
                  {c.owning_department_code ? (
                    <>
                      <span className="font-mono">{c.owning_department_code}</span>
                      <span className="ml-1 text-foreground">{c.owning_department_name}</span>
                    </>
                  ) : (
                    '—'
                  )}
                </div>
                <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    Limit:{' '}
                    {c.monthly_limit_vnd != null ? formatVnd(c.monthly_limit_vnd) : 'Not set'}
                  </span>
                  <span>{c.tx_count_this_month} tx/month</span>
                </div>
                <div className="flex gap-2">
                  <Link
                    to={`/admin/cards/${c.id}/edit`}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                  >
                    <Pencil size={12} strokeWidth={1.75} /> Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Soft-delete card ${c.bank_code}•${c.last4}?`))
                        remove.mutate(c.id);
                    }}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 size={12} strokeWidth={1.75} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
